import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle, Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { trackPurchase } from "@/lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking, success, error
  const [bookingId, setBookingId] = useState(null);
  const [purchaseFired, setPurchaseFired] = useState(false);
  const sessionId = searchParams.get("session_id");
  const provider = searchParams.get("provider") || "stripe";

  useEffect(() => {
    if (sessionId) {
      if (provider === "razorpay") {
        // Razorpay payment already verified in handler before redirect — mark success
        setStatus("success");
        // Extract booking_id from tx record via API
        fetchRazorpayBooking(sessionId);
      } else {
        pollPaymentStatus(sessionId, 0);
      }
    }
  }, [sessionId, provider]);

  // Fire GA4 purchase event once we have a confirmed booking_id (only once).
  useEffect(() => {
    if (status !== "success" || !bookingId || purchaseFired) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/bookings/${bookingId}`);
        const b = res.data || {};
        trackPurchase({
          bookingId: b.id || bookingId,
          packageId: b.package_id,
          packageName: b.package_name,
          amountInr: b.total_amount,
          quantity: (b.num_adults || 1) + (b.num_children || 0),
        });
        setPurchaseFired(true);
      } catch (_) {
        // Non-blocking — analytics only
      }
    })();
  }, [status, bookingId, purchaseFired]);

  const fetchRazorpayBooking = async (orderId) => {
    // Query the /checkout/status endpoint — it works for both since we use session_id (order_id for razorpay)
    try {
      const response = await axios.get(`${API}/checkout/status/${orderId}`);
      setBookingId(response.data.booking_id);
    } catch (e) {
      // Fallback: just show success without booking ID
    }
  };

  const pollPaymentStatus = async (sessionId, attempt) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempt >= maxAttempts) {
      setStatus("error");
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === "paid" || response.data.payment_status === "completed") {
        setStatus("success");
        setBookingId(response.data.booking_id);
        return;
      } else if (response.data.status === "expired") {
        setStatus("error");
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      setStatus("error");
    }
  };

  return (
    <div data-testid="payment-success-page" className="grain-texture min-h-screen">
      <Navbar />

      <section className="py-32 bg-background">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {status === "checking" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="payment-checking"
            >
              <Loader className="w-16 h-16 mx-auto mb-6 text-secondary animate-spin" />
              <h1 className="text-3xl font-serif mb-4">Processing Your Payment...</h1>
              <p className="text-muted-foreground">Please wait while we confirm your booking.</p>
            </motion.div>
          )}

          {status === "success" && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              data-testid="payment-success"
            >
              <div className="bg-green-100 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h1 className="text-4xl font-serif mb-4 text-green-700">Payment Successful!</h1>
              <p className="text-lg text-muted-foreground mb-6">
                Your booking has been confirmed. We've sent a confirmation email with all the details.
              </p>
              <div className="bg-muted p-6 rounded-lg mb-8">
                <p className="text-sm text-muted-foreground mb-2">Booking Reference</p>
                <p className="text-xl font-bold text-primary">{bookingId}</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate("/packages")}
                  data-testid="explore-more-btn"
                  className="bg-primary hover:bg-primary/90"
                >
                  Explore More Packages
                </Button>
                <Button
                  onClick={() => navigate("/home")}
                  data-testid="back-home-btn"
                  variant="outline"
                >
                  Back to Home
                </Button>
              </div>
            </motion.div>
          )}

          {status === "error" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="payment-error"
            >
              <h1 className="text-3xl font-serif mb-4 text-red-600">Payment Verification Failed</h1>
              <p className="text-muted-foreground mb-8">
                We couldn't verify your payment. Please check your email for confirmation or contact us.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate("/contact")}
                  data-testid="contact-us-btn"
                  className="bg-primary hover:bg-primary/90"
                >
                  Contact Us
                </Button>
                <Button
                  onClick={() => navigate("/packages")}
                  data-testid="try-again-btn"
                  variant="outline"
                >
                  Try Again
                </Button>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PaymentSuccess;